import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { environment } from '../../../../environments/environment';

interface ExtensionStatus {
  extensionNumber: string;
  pool: string;
  poolLabel: string;
  status: 'AVAILABLE' | 'RESERVED';
  reservedByUserId: number | null;
  reservedByUserName: string | null;
  reservedAt: string | null;
}

interface PoolGroup {
  pool: string;
  poolLabel: string;
  icon: string;
  avatarClass: string;
  range: string;
  exts: ExtensionStatus[];
}

const POOL_ORDER = ['CSR', 'SUPERVISOR', 'ADMIN', 'MANAGER', 'INTERNAL_USER'];

@Component({
  selector: 'app-csr-extension-status',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './csr-extension-status.component.html',
  styleUrls: ['./csr-extension-status.component.scss'],
})
export class CsrExtensionStatusComponent implements OnInit, OnDestroy {
  extensions: ExtensionStatus[] = [];
  loading = true;
  lastRefreshed: Date | null = null;
  currentUserRole = '';
  readonly releasingExts = new Set<string>();
  releaseError = '';
  confirmExt: ExtensionStatus | null = null;
  private refreshInterval: any = null;

  get isAdmin(): boolean {
    return this.currentUserRole === 'ADMIN' || this.currentUserRole === 'SUPER_ADMIN';
  }

  get groupedPools(): PoolGroup[] {
    const map = new Map<string, PoolGroup>();
    for (const e of this.extensions) {
      let group = map.get(e.pool);
      if (!group) {
        group = {
          pool: e.pool,
          poolLabel: e.poolLabel || e.pool,
          icon: poolIcon(e.pool),
          avatarClass: poolAvatarClass(e.pool),
          range: '',
          exts: [],
        };
        map.set(e.pool, group);
      }
      group.exts.push(e);
    }
    for (const g of map.values()) {
      const nums = g.exts.map(e => e.extensionNumber).sort((a, b) => a.localeCompare(b));
      g.range = nums.length > 1 ? `${nums[0]}–${nums.at(-1) ?? ''}` : (nums[0] ?? '');
    }
    return [...map.values()].sort((a, b) => {
      const ai = POOL_ORDER.indexOf(a.pool);
      const bi = POOL_ORDER.indexOf(b.pool);
      return (ai < 0 ? 99 : ai) - (bi < 0 ? 99 : bi);
    });
  }

  get totalReserved()  { return this.extensions.filter(e => e.status === 'RESERVED').length; }
  get totalAvailable() { return this.extensions.filter(e => e.status === 'AVAILABLE').length; }

  ngOnInit() {
    try {
      const perm = localStorage.getItem('permissions');
      if (perm) this.currentUserRole = JSON.parse(perm)?.role ?? '';
    } catch { /* ignore parse errors */ }
    void this.load();
    this.refreshInterval = setInterval(() => void this.load(), 30000);
  }

  ngOnDestroy() {
    if (this.refreshInterval) clearInterval(this.refreshInterval);
  }

  async load() {
    const token = localStorage.getItem('auth-token') ?? '';
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await fetch(`${environment.apiHost}/api/v1/telephony/extensions/status`, { headers });
      if (!res.ok) return;
      const json: any = await res.json();
      this.extensions = json?.data ?? json ?? [];
      this.lastRefreshed = new Date();
    } catch { /* silently ignore */ }
    finally { this.loading = false; }
  }

  promptRelease(ext: ExtensionStatus): void {
    if (!this.isAdmin || ext.status !== 'RESERVED') return;
    this.confirmExt = ext;
  }

  cancelRelease(): void {
    this.confirmExt = null;
  }

  async confirmRelease(): Promise<void> {
    const ext = this.confirmExt;
    if (!ext) return;
    this.confirmExt = null;

    this.releasingExts.add(ext.extensionNumber);
    this.releaseError = '';
    const token = localStorage.getItem('auth-token') ?? '';
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      const res = await fetch(
        `${environment.apiHost}/api/v1/telephony/extensions/${ext.extensionNumber}/force-release`,
        { method: 'POST', headers }
      );
      if (res.ok) {
        await this.load();
      } else {
        const err: any = await res.json().catch(() => ({}));
        this.releaseError = err?.message ?? `HTTP ${res.status}`;
      }
    } catch (e: any) {
      this.releaseError = e?.message ?? 'Network error';
    } finally {
      this.releasingExts.delete(ext.extensionNumber);
    }
  }

  reservedSince(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) +
           ' · ' + d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }

  initials(name: string | null): string {
    if (!name) return '?';
    return name.split(' ').map(n => n[0] ?? '').join('').toUpperCase().slice(0, 2);
  }
}

function poolIcon(pool: string): string {
  switch (pool) {
    case 'CSR':           return 'headset_mic';
    case 'SUPERVISOR':    return 'supervisor_account';
    case 'ADMIN':         return 'admin_panel_settings';
    case 'MANAGER':       return 'manage_accounts';
    case 'INTERNAL_USER': return 'person';
    default:              return 'dialpad';
  }
}

function poolAvatarClass(pool: string): string {
  switch (pool) {
    case 'SUPERVISOR':    return 'user-avatar-supervisor';
    case 'ADMIN':         return 'user-avatar-admin';
    case 'MANAGER':       return 'user-avatar-manager';
    case 'INTERNAL_USER': return 'user-avatar-internal';
    default:              return '';
  }
}
